import { Typography, Box } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';

interface Props {
  value: number;
  color: string;
  maxValue?: number;
  fontSize?: number;
}

const AutoScaleNumber = ({ value, color, maxValue, fontSize }: Props) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const measureRef = useRef<HTMLSpanElement | null>(null);
    const [autoFontSize, setAutoFontSize] = useState(48);
    const renderedFontSize = fontSize ?? autoFontSize;

    useLayoutEffect(() => {
    if (fontSize) return;

    const container = containerRef.current;
    const measure = measureRef.current;

    if (!container || !measure) return;

    let frame: number;

    const fitText = () => {
        cancelAnimationFrame(frame);

        frame = requestAnimationFrame(() => {
        const containerWidth = container.offsetWidth;
        const usedValue = maxValue != null ? maxValue : value;
        const textValue = usedValue.toLocaleString('en-US');

        measure.textContent = textValue;
        measure.style.fontSize = '48px';

        let size = 48;

        while (
            measure.scrollWidth > containerWidth &&
            size > 24
        ) {
            size -= 1;
            measure.style.fontSize = size + 'px';
        }

        setAutoFontSize(prev => (prev !== size ? size : prev));
        });
    };

    fitText();

    const observer = new ResizeObserver(fitText);
    observer.observe(container);

    return () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
    };
    }, [value, maxValue]);

    return (
    <Box ref={containerRef} sx={{ width: '100%', position: 'relative' }}>
        <Typography
        sx={{
            fontSize: renderedFontSize,
            fontWeight: 500,
            lineHeight: 1.1,
            color,
            whiteSpace: 'nowrap',  
        }}
        >
        {value.toLocaleString('en-US')}
        </Typography>
        <Box
          ref={measureRef}
          component="span"
          sx={{
            visibility: 'hidden',
            position: 'absolute',
            top: 0,
            left: 0,
            whiteSpace: 'nowrap',
            fontWeight: 500,
            lineHeight: 1.1,
            overflow: 'visible',
        }}
        />
    </Box>
    );
};

export default AutoScaleNumber;